import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import DataTable from 'components/shared/DataTable';
import Foldout from 'components/shared/Foldout';
import { adminLocationsQueryKeys, adminShowLocation } from 'services/api/admin/locations';
import { colors } from 'styles/theme/colors';

type Group = {
  id: string;
  name: string;
  groupType?: {
    id: string;
    displayName: string;
  };
};

type Props = {
  locationId: string;
  locationName: string;
  handleClose: () => void;
};

const columnHelper = createColumnHelper<Group>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Group Name',
    cell: (data) => (
      <BoldText>
        {data.getValue()}
        {data.row.original.groupType?.displayName && ` (${data.row.original.groupType.displayName})`}
      </BoldText>
    ),
    enableSorting: false,
  }),
];

export default function AssociatedGroupsSlideout({ locationId, locationName, handleClose }: Props) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setOpen(true));
  }, []);

  const animateClose = () => {
    setOpen(false);
    setTimeout(handleClose, 300);
  };

  const { data: location, isLoading } = useQuery({
    queryKey: adminLocationsQueryKeys.show(locationId),
    queryFn: ({ signal }) => adminShowLocation(locationId, signal, ['groups']),
    enabled: !!locationId,
  });

  const groups = location?.groups ?? [];
  const title = `${locationName} - Associated Groups`;

  return (
    <Foldout open={open} position='right' onBackdropClick={animateClose} width='min(80%, 480px)'>
      <PanelContainer>
        <PanelHeader>
          <PanelTitle>{title}</PanelTitle>
          <CloseButton onClick={animateClose}>&times;</CloseButton>
        </PanelHeader>
        <PanelContent>
          <ItemsCount>
            {groups.length} Associated Group{groups.length !== 1 ? 's' : ''}
          </ItemsCount>
          <DataTable
            columns={columns}
            data={groups}
            loading={isLoading}
            defaultSortBy={[{ id: 'name', desc: false }]}
          />
        </PanelContent>
        <PanelFooter>
          <ButtonGroup>
            <Button onClick={animateClose}>Close</Button>
          </ButtonGroup>
        </PanelFooter>
      </PanelContainer>
    </Foldout>
  );
}

const BoldText = styled.span`
  font-weight: 700;
`;

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${colors.black50};
  line-height: 1;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PanelFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
  display: flex;
  justify-content: flex-end;
`;

const ItemsCount = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: ${colors.black};
`;
